import React, { useState, useCallback } from "react";
import { API_URL } from "../config";
import PageHeader from "./PageHeader";

// Obnova ze zálohy – admin nástroj.
// Nahraje zálohu z MongoDB (.tar mongodump / .json export z CRM / mongoexport),
// umožní porovnat konkrétní IČO se stavem v CRM (vč. duplicit) a synchronizovat
// aktivity / vlastnictví. Dále porovná firmy konkrétního uživatele.

export default function RestoreFromBackup({ userId, isDarkMode = false, onRestore }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [upload, setUpload] = useState(null); // { uploadId, filename, totalCompanies, ... }
  const [error, setError] = useState("");
  const [tab, setTab] = useState("ico"); // 'ico' | 'user'

  // --- Porovnání podle IČO ---
  const [ico, setIco] = useState("");
  const [icoLoading, setIcoLoading] = useState(false);
  const [icoResult, setIcoResult] = useState(null); // { ico, backup:[], live:[] }
  const [syncOwnership, setSyncOwnership] = useState(false);
  const [createIfMissing, setCreateIfMissing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [syncing, setSyncing] = useState(false);

  // --- Porovnání podle uživatele ---
  const [backupUsers, setBackupUsers] = useState([]);
  const [selectedBackupUser, setSelectedBackupUser] = useState("");
  const [userResult, setUserResult] = useState(null);
  const [userLoading, setUserLoading] = useState(false);

  const uid = userId || localStorage.getItem("lastUserId") || "";

  // Bezpečné zpracování odpovědi – server může vrátit HTML (404/500), pak by
  // res.json() spadl s nejasnou hláškou "The string did not match the expected pattern".
  const parseResponse = async (res) => {
    const raw = await res.text();
    let data = null;
    try {
      data = raw ? JSON.parse(raw) : null;
    } catch (_) {
      data = null;
    }
    if (!res.ok) {
      const msg =
        (data && data.message) ||
        (raw && raw.slice(0, 200).replace(/<[^>]*>/g, "").trim()) ||
        `HTTP ${res.status}`;
      const files = data && data.foundFiles && data.foundFiles.length
        ? ` (nalezené soubory: ${data.foundFiles.join(", ")})`
        : "";
      throw new Error(`HTTP ${res.status}: ${msg}${files}`);
    }
    if (data === null) {
      throw new Error(
        "Server nevrátil platnou odpověď (JSON). Zkontrolujte, že je backend nasazený s modulem obnovy ze zálohy."
      );
    }
    return data;
  };

  const doUpload = useCallback(async () => {
    if (!file) return;
    setUploading(true);
    setError("");
    setUpload(null);
    setIcoResult(null);
    setUserResult(null);
    setBackupUsers([]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_URL}/backup-restore/upload?userId=${uid}`, {
        method: "POST",
        headers: { "X-User-Id": uid },
        body: fd,
      });
      const data = await parseResponse(res);
      setUpload(data);
    } catch (err) {
      setError("Nahrání zálohy selhalo: " + (err.message || err));
    } finally {
      setUploading(false);
    }
  }, [file, uid]);

  const searchIco = useCallback(async () => {
    if (!upload || !ico.trim()) return;
    setIcoLoading(true);
    setError("");
    setSyncMsg("");
    setIcoResult(null);
    try {
      const res = await fetch(
        `${API_URL}/backup-restore/${upload.uploadId}/company?ico=${encodeURIComponent(ico.trim())}&userId=${uid}`,
        { headers: { "X-User-Id": uid } }
      );
      const data = await parseResponse(res);
      setIcoResult(data);
    } catch (err) {
      setError("Porovnání selhalo: " + (err.message || err));
    } finally {
      setIcoLoading(false);
    }
  }, [upload, ico, uid]);

  const doSync = useCallback(async () => {
    if (!upload || !icoResult) return;
    const confirmMsg =
      `Opravdu synchronizovat IČO ${icoResult.ico}?\n\n` +
      `• Aktivity ze zálohy budou doplněny (bez mazání stávajících)\n` +
      (syncOwnership ? "• Vlastník bude obnoven ze zálohy\n" : "") +
      (createIfMissing ? "• Chybějící firma bude vytvořena ze zálohy\n" : "");
    if (!window.confirm(confirmMsg)) return;
    setSyncing(true);
    setSyncMsg("");
    setError("");
    try {
      const res = await fetch(`${API_URL}/backup-restore/${upload.uploadId}/sync?userId=${uid}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-User-Id": uid },
        body: JSON.stringify({
          ico: icoResult.ico,
          syncActivities: true,
          syncOwnership,
          createIfMissing,
        }),
      });
      const data = await parseResponse(res);
      const parts = [];
      if (data.createdCompany) parts.push("firma vytvořena");
      if (data.activitiesAdded) parts.push(`${data.activitiesAdded} aktivit doplněno`);
      if (data.ownershipChanged) parts.push("vlastník obnoven");
      setSyncMsg("✅ Synchronizováno: " + (parts.length ? parts.join(", ") : "žádné změny (vše již shodné)"));
      if (onRestore) onRestore();
      // Obnov porovnání
      searchIco();
    } catch (err) {
      setError("Synchronizace selhala: " + (err.message || err));
    } finally {
      setSyncing(false);
    }
  }, [upload, icoResult, syncOwnership, createIfMissing, uid, onRestore, searchIco]);

  const loadBackupUsers = useCallback(async () => {
    if (!upload) return;
    try {
      const res = await fetch(`${API_URL}/backup-restore/${upload.uploadId}/users?userId=${uid}`, {
        headers: { "X-User-Id": uid },
      });
      const data = await parseResponse(res);
      setBackupUsers(data.users || []);
    } catch (err) {
      /* ignore */
    }
  }, [upload, uid]);

  const searchUser = useCallback(async () => {
    if (!upload || !selectedBackupUser) return;
    setUserLoading(true);
    setError("");
    setUserResult(null);
    try {
      const res = await fetch(
        `${API_URL}/backup-restore/${upload.uploadId}/user-companies?backupUserId=${encodeURIComponent(selectedBackupUser)}&userId=${uid}`,
        { headers: { "X-User-Id": uid } }
      );
      const data = await parseResponse(res);
      setUserResult(data);
    } catch (err) {
      setError("Porovnání uživatele selhalo: " + (err.message || err));
    } finally {
      setUserLoading(false);
    }
  }, [upload, selectedBackupUser, uid]);

  const card = isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const text = isDarkMode ? "text-gray-200" : "text-gray-800";
  const sub = isDarkMode ? "text-gray-400" : "text-gray-500";
  const inputCls = `border rounded px-3 py-2 ${isDarkMode ? "bg-gray-900 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"}`;

  const CompanyTable = ({ rows, title, accent }) => (
    <div className="flex-1 min-w-0">
      <div className={`font-semibold mb-2 ${text}`}>{title} <span className={sub}>({rows.length})</span></div>
      {rows.length === 0 ? (
        <div className={`text-sm ${sub} italic py-3`}>Žádné záznamy</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className={`${isDarkMode ? "bg-gray-700" : "bg-gray-100"} ${text}`}>
                <th className="text-left px-2 py-1">Název</th>
                <th className="text-left px-2 py-1">Vlastník</th>
                <th className="text-center px-2 py-1">Aktivity</th>
                <th className="text-center px-2 py-1">Kontakty</th>
                <th className="text-left px-2 py-1">Šarže / Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c, i) => (
                <tr key={c._id || i} className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"} ${text}`}>
                  <td className="px-2 py-1">{c.name || "—"}</td>
                  <td className="px-2 py-1">{c.ownerName || <span className={sub}>{c.ownerId ? c.ownerId.slice(-6) : "—"}</span>}</td>
                  <td className={`px-2 py-1 text-center font-semibold ${accent}`}>{c.activitiesCount}</td>
                  <td className="px-2 py-1 text-center">{c.contactsCount}</td>
                  <td className="px-2 py-1">{c.batchName || c.status || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"} pb-16`}>
      <div className="max-w-6xl mx-auto px-4 pt-4">
        <PageHeader
          title="♻️ Obnova ze zálohy"
          subtitle="Porovnání a synchronizace dat z MongoDB zálohy se živým CRM"
        />

        {error && (
          <div className="mb-4 p-3 rounded bg-red-100 border border-red-300 text-red-800 text-sm">{error}</div>
        )}

        {/* Nahrání zálohy */}
        <div className={`border rounded-lg p-4 mb-6 ${card}`}>
          <div className={`font-semibold mb-3 ${text}`}>1. Nahrát zálohu</div>
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept=".tar,.gz,.tgz,.json,.bson"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className={`text-sm ${text}`}
            />
            <button
              onClick={doUpload}
              disabled={!file || uploading}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded font-semibold text-sm"
            >
              {uploading ? "Nahrávám…" : "Nahrát a načíst"}
            </button>
          </div>
          <div className={`text-xs mt-2 ${sub}`}>
            Podporováno: .tar (mongodump), .json (export z CRM), mongoexport JSON/NDJSON, .gz komprese.
          </div>
          {upload && (
            <div className={`mt-3 text-sm p-3 rounded ${isDarkMode ? "bg-gray-900" : "bg-green-50"} ${text}`}>
              ✅ Načteno: <b>{upload.filename}</b> — {upload.totalCompanies} firem
              ({upload.companiesWithIco} s IČO), {upload.totalUsers} uživatelů. Formát: {upload.parsedFrom}.
            </div>
          )}
        </div>

        {upload && (
          <>
            {/* Přepínač */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setTab("ico")}
                className={`px-4 py-2 rounded font-semibold text-sm ${tab === "ico" ? "bg-blue-600 text-white" : isDarkMode ? "bg-gray-700 text-gray-200" : "bg-gray-200 text-gray-700"}`}
              >
                Porovnat podle IČO
              </button>
              <button
                onClick={() => { setTab("user"); if (!backupUsers.length) loadBackupUsers(); }}
                className={`px-4 py-2 rounded font-semibold text-sm ${tab === "user" ? "bg-blue-600 text-white" : isDarkMode ? "bg-gray-700 text-gray-200" : "bg-gray-200 text-gray-700"}`}
              >
                Porovnat podle uživatele
              </button>
            </div>

            {tab === "ico" && (
              <div className={`border rounded-lg p-4 mb-6 ${card}`}>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <input
                    type="text"
                    value={ico}
                    onChange={(e) => setIco(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && searchIco()}
                    placeholder="Zadejte IČO (např. 70327271)"
                    className={inputCls}
                  />
                  <button
                    onClick={searchIco}
                    disabled={icoLoading || !ico.trim()}
                    className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded font-semibold text-sm"
                  >
                    {icoLoading ? "Hledám…" : "Porovnat"}
                  </button>
                </div>

                {icoResult && (
                  <>
                    <div className="flex flex-col lg:flex-row gap-6 mb-4">
                      <CompanyTable rows={icoResult.backup} title="📦 V ZÁLOZE" accent="text-green-500" />
                      <CompanyTable rows={icoResult.live} title="🟢 V CRM (aktuálně)" accent="text-blue-500" />
                    </div>

                    <div className={`border-t pt-3 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                      <div className={`font-semibold mb-2 ${text}`}>Synchronizace</div>
                      <div className="flex flex-wrap items-center gap-4 mb-3">
                        <label className={`flex items-center gap-2 text-sm ${text}`}>
                          <input type="checkbox" checked disabled /> Doplnit aktivity (append-only)
                        </label>
                        <label className={`flex items-center gap-2 text-sm ${text}`}>
                          <input type="checkbox" checked={syncOwnership} onChange={(e) => setSyncOwnership(e.target.checked)} />
                          Obnovit vlastníka ze zálohy
                        </label>
                        <label className={`flex items-center gap-2 text-sm ${text}`}>
                          <input type="checkbox" checked={createIfMissing} onChange={(e) => setCreateIfMissing(e.target.checked)} />
                          Vytvořit chybějící firmu
                        </label>
                        <button
                          onClick={doSync}
                          disabled={syncing}
                          className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-2 rounded font-semibold text-sm"
                        >
                          {syncing ? "Synchronizuji…" : "▶ Synchronizovat do CRM"}
                        </button>
                      </div>
                      {syncMsg && <div className={`text-sm ${text}`}>{syncMsg}</div>}
                    </div>
                  </>
                )}
              </div>
            )}

            {tab === "user" && (
              <div className={`border rounded-lg p-4 mb-6 ${card}`}>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <select
                    value={selectedBackupUser}
                    onChange={(e) => setSelectedBackupUser(e.target.value)}
                    className={inputCls}
                  >
                    <option value="">— vyberte uživatele ze zálohy —</option>
                    {backupUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name || u.id.slice(-6)} ({u.backupCompanies} firem v záloze)
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={searchUser}
                    disabled={userLoading || !selectedBackupUser}
                    className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded font-semibold text-sm"
                  >
                    {userLoading ? "Hledám…" : "Porovnat"}
                  </button>
                </div>

                {userResult && (
                  <>
                    <div className={`text-sm mb-3 ${text}`}>
                      V záloze: <b>{userResult.backupCount}</b> firem &nbsp;|&nbsp; Aktuálně v CRM: <b>{userResult.liveCount}</b> firem
                    </div>
                    <div className="flex flex-col lg:flex-row gap-6 mb-4">
                      <CompanyTable rows={userResult.backup} title="📦 V ZÁLOZE (na uživateli)" accent="text-green-500" />
                      <CompanyTable rows={userResult.live} title="🟢 V CRM (na uživateli)" accent="text-blue-500" />
                    </div>
                    {userResult.missingFromUser?.length > 0 && (
                      <div className="mb-4">
                        <CompanyTable
                          rows={userResult.missingFromUser}
                          title="⚠️ CHYBÍ (byly v záloze, nyní nejsou u tohoto uživatele)"
                          accent="text-red-500"
                        />
                      </div>
                    )}
                    {userResult.ownedByOthersNow?.length > 0 && (
                      <div className="mb-2">
                        <CompanyTable
                          rows={userResult.ownedByOthersNow}
                          title="🔁 NYNÍ U JINÉHO VLASTNÍKA"
                          accent="text-amber-500"
                        />
                      </div>
                    )}
                    <div className={`text-xs mt-2 ${sub}`}>
                      Tip: konkrétní firmu obnovíte/synchronizujete v záložce „Porovnat podle IČO“.
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
